import {
  ENDPOINT_GROUP_OPTIONS,
  SUBJECT_TYPE_OPTIONS,
  WINDOW_SECONDS_OPTIONS,
} from './publicApiAccessPageUtils'

function asText(value) {
  return String(value ?? '').trim()
}

function toDateInputValue(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function toNumberOrNull(value) {
  const raw = asText(value)
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export const EMPTY_POLICY_FORM = {
  subjectType: 'global',
  subjectId: '',
  endpointGroup: 'public_recognition',
  windowSeconds: '3600',
  maxRequests: '20',
  maxCostUnits: '100',
  maxConcurrentJobs: '2',
  dailyMaxRequests: '50',
  monthlyMaxRequests: '',
  status: 'active',
  priority: '100',
  reason: '',
  effectiveFrom: '',
  effectiveTo: '',
}

export function cloneEmptyPolicyForm() {
  return { ...EMPTY_POLICY_FORM }
}

export function mapPolicyToForm(policy = {}) {
  return {
    subjectType: asText(policy.subjectType) || 'global',
    subjectId: policy.subjectId == null ? '' : asText(policy.subjectId),
    endpointGroup: asText(policy.endpointGroup) || 'public_recognition',
    windowSeconds: asText(policy.windowSeconds) || '3600',
    maxRequests: asText(policy.maxRequests) || '0',
    maxCostUnits: policy.maxCostUnits == null ? '' : asText(policy.maxCostUnits),
    maxConcurrentJobs: policy.maxConcurrentJobs == null ? '' : asText(policy.maxConcurrentJobs),
    dailyMaxRequests: policy.dailyMaxRequests == null ? '' : asText(policy.dailyMaxRequests),
    monthlyMaxRequests: policy.monthlyMaxRequests == null ? '' : asText(policy.monthlyMaxRequests),
    status: asText(policy.status) || 'active',
    priority: asText(policy.priority) || '100',
    reason: policy.reason == null ? '' : asText(policy.reason),
    effectiveFrom: toDateInputValue(policy.effectiveFrom),
    effectiveTo: toDateInputValue(policy.effectiveTo),
  }
}

export function requiresSubjectId(subjectType) {
  const normalized = asText(subjectType)
  return normalized === 'user' || normalized === 'mobile_device' || normalized === 'external_identity'
}

export function validatePolicyForm(form) {
  const errors = {}
  const subjectType = asText(form.subjectType)
  const endpointGroup = asText(form.endpointGroup)
  const windowSeconds = Number(form.windowSeconds)
  const maxRequests = Number(form.maxRequests)
  const maxCostUnits = toNumberOrNull(form.maxCostUnits)
  const maxConcurrentJobs = toNumberOrNull(form.maxConcurrentJobs)
  const dailyMaxRequests = toNumberOrNull(form.dailyMaxRequests)
  const monthlyMaxRequests = toNumberOrNull(form.monthlyMaxRequests)
  const priority = Number(form.priority)
  const effectiveFrom = form.effectiveFrom ? new Date(form.effectiveFrom) : null
  const effectiveTo = form.effectiveTo ? new Date(form.effectiveTo) : null

  if (!SUBJECT_TYPE_OPTIONS.includes(subjectType)) errors.subjectType = 'Choose a valid subject type.'
  if (!ENDPOINT_GROUP_OPTIONS.includes(endpointGroup)) errors.endpointGroup = 'Choose a valid endpoint group.'
  if (!WINDOW_SECONDS_OPTIONS.includes(windowSeconds)) errors.windowSeconds = 'Choose 60, 3600, 86400, or 2592000.'
  if (!Number.isInteger(maxRequests) || maxRequests <= 0) errors.maxRequests = 'Max requests must be a positive integer.'
  if (maxCostUnits != null && (!Number.isInteger(maxCostUnits) || maxCostUnits < 0)) errors.maxCostUnits = 'Cost units must be a non-negative integer.'
  if (maxConcurrentJobs != null && (!Number.isInteger(maxConcurrentJobs) || maxConcurrentJobs < 0)) errors.maxConcurrentJobs = 'Concurrent jobs must be a non-negative integer.'
  if (dailyMaxRequests != null && (!Number.isInteger(dailyMaxRequests) || dailyMaxRequests < 0)) errors.dailyMaxRequests = 'Daily quota must be a non-negative integer.'
  if (monthlyMaxRequests != null && (!Number.isInteger(monthlyMaxRequests) || monthlyMaxRequests < 0)) errors.monthlyMaxRequests = 'Monthly quota must be a non-negative integer.'
  if (!Number.isInteger(priority) || priority < 0) errors.priority = 'Priority must be a non-negative integer.'
  if (requiresSubjectId(subjectType) && !asText(form.subjectId)) errors.subjectId = 'Subject id is required for this subject type.'
  if (effectiveFrom && Number.isNaN(effectiveFrom.getTime())) errors.effectiveFrom = 'Enter a valid start date.'
  if (effectiveTo && Number.isNaN(effectiveTo.getTime())) errors.effectiveTo = 'Enter a valid end date.'
  if (effectiveFrom && effectiveTo && !Number.isNaN(effectiveFrom.getTime()) && !Number.isNaN(effectiveTo.getTime()) && effectiveTo <= effectiveFrom) {
    errors.effectiveTo = 'Effective to must be after effective from.'
  }

  return errors
}

export function buildPolicyPayload(form) {
  return {
    subjectType: asText(form.subjectType),
    subjectId: asText(form.subjectId) || null,
    endpointGroup: asText(form.endpointGroup),
    windowSeconds: Number(form.windowSeconds),
    maxRequests: Number(form.maxRequests),
    maxCostUnits: toNumberOrNull(form.maxCostUnits),
    maxConcurrentJobs: toNumberOrNull(form.maxConcurrentJobs),
    dailyMaxRequests: toNumberOrNull(form.dailyMaxRequests),
    monthlyMaxRequests: toNumberOrNull(form.monthlyMaxRequests),
    status: asText(form.status) || 'active',
    priority: Number(form.priority),
    reason: asText(form.reason) || null,
    effectiveFrom: asText(form.effectiveFrom) || null,
    effectiveTo: asText(form.effectiveTo) || null,
  }
}

export function normalizePolicyFormEventValue(name, value) {
  if (name === 'reason' || name === 'subjectId') return value
  if (name === 'effectiveFrom' || name === 'effectiveTo') return value
  return value
}
